import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import Text from 'src/components/ui/Text'
import { localeAtom } from 'src/stores/locale'
import { formatCurrency, useCurrency } from 'src/utils/currency'
import type { ExtraPackage } from '../api/types'
import { OneTimePackCard } from '../components/OneTimePackCard'
import { PackSectionHeader } from '../components/PackSectionHeader'
import { getOneTimeCardDisplayData } from '../domain/oneTimeCardData'

interface OneTimePackSectionProps {
  packs: ExtraPackage[]
  showingSuccessId: string | null
  currentTier: number
  selectedTier: number
  onPurchase: (pack: ExtraPackage) => void
  onRequireUpgradeClick?: () => void
}

/**
 * Renders a `<PackSectionHeader>` (a `<li>`) followed by the pack cards as
 * direct children of the parent `<ul>`.
 */
export function OneTimePackSection(props: OneTimePackSectionProps) {
  const { t } = useTranslation(['membership', 'user'])
  const [currency] = useCurrency()
  const locale = useAtomValue(localeAtom)

  if (props.packs.length === 0) return null

  const headerLabel = (
    <Text
      i18nKey="membership:extra-pack.groups.one-time.treatment.label"
      defaults="Limited Time Offers"
    />
  )

  const cards = props.packs.flatMap(pack => {
    const tierValue = pack.values[props.selectedTier] ?? pack.values[0]
    if (!tierValue) return []
    const purchasable = props.currentTier === props.selectedTier
    const cardData = getOneTimeCardDisplayData({
      marketingMetadata: pack.marketingMetadata,
      packageContents: pack.packageContents ?? [],
      t,
    })

    return (
      <OneTimePackCard
        key={pack.id}
        title={cardData.title}
        rows={cardData.rows}
        decorations={cardData.decorations}
        multiplier={cardData.multiplier}
        boostedCredits={tierValue.creditsAfterBoosts}
        priceLabel={formatCurrency(pack.prices[currency], { currency })}
        locale={locale}
        animateCredits={props.currentTier === 0}
        disabled={!purchasable && Boolean(props.onRequireUpgradeClick)}
        showSuccess={props.showingSuccessId === pack.id}
        onPurchase={
          purchasable
            ? () => props.onPurchase(pack)
            : props.onRequireUpgradeClick
        }
      />
    )
  })

  return (
    <>
      <PackSectionHeader>{headerLabel}</PackSectionHeader>
      {cards}
    </>
  )
}
